/**
 * @voltxampere/components — MOSFET (NMOS + PMOS, Shichman-Hodges)
 *
 * Sprint 7 promotes the Sprint 4 placeholder `mosfet` type into
 * canonical NMOS / PMOS flavours with a full SPICE Level 1
 * parameter set. The pin layout stays 3-pin (gate / drain / source)
 * — the body terminal will land with BSIM3/4 in Faz 2.
 *
 * Shichman-Hodges parameters (aka SPICE Level 1, Gray & Meyer Ch. 3):
 *
 *   - Kp     : transconductance parameter μ_n · C_ox (A/V²)
 *   - Vt0    : zero-bias threshold voltage (V)
 *              NMOS: typically positive (e.g. +0.7 V)
 *              PMOS: typically negative (e.g. -0.7 V)
 *   - Lambda : channel-length modulation (1/V) — 0 disables CLM
 *   - W      : channel width (m)
 *   - L      : channel length (m)
 *
 * Id in saturation:
 *
 *   Id = (Kp / 2) · (W/L) · (Vgs - Vt0)² · (1 + λ Vds)     (NMOS)
 *
 * PMOS mirror uses |Vgs - Vt0|² with source-referenced conventions.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { z } from "zod";
/**
 * Shared SPICE Level 1 parameter set. Both polarities consume this
 * schema; the sign of `Vt0` encodes the device polarity at a
 * parameter level too (NMOS Vt0 > 0, PMOS Vt0 < 0) — the stamp
 * still reads the component **type** to pick the sign convention
 * for `Vgs` / `Vds`.
 */
export const MosfetParamsSchema = z
    .object({
    /** Transconductance parameter μ·C_ox (A/V²). Default 2e-5 —
     *  representative of a 1 µm process. */
    Kp: z.number().positive().finite().default(2e-5),
    /** Zero-bias threshold voltage (V). Default sign follows the
     *  device polarity — convention: NMOS +0.7 V, PMOS -0.7 V. The
     *  stamp uses this verbatim; callers MUST set it correctly for
     *  PMOS. */
    Vt0: z.number().finite().default(0.7),
    /** Channel-length modulation (1/V). Default 0.01. Use 0 to
     *  disable (ideal square-law saturation). */
    Lambda: z.number().nonnegative().finite().default(0.01),
    /** Channel width W (m). Default 10 µm. */
    W: z.number().positive().finite().default(10e-6),
    /** Channel length L (m). Default 1 µm — Sprint 7 matches a
     *  generic 1 µm process. */
    L: z.number().positive().finite().default(1e-6),
    /**
     * Sprint 22 D4 / D-22-6 — gate-capacitance (Meyer model)
     * parameters (zero-default, additive).
     *
     *   Cgs0:  per-width gate-source overlap cap        (F/m)
     *   Cgd0:  per-width gate-drain overlap cap         (F/m)
     *   Cgb0:  per-length gate-bulk overlap cap         (F/m)  (unused — body terminal pending)
     *   Cox:   per-area gate-oxide capacitance density  (F/m²)
     *
     * Total AC gate capacitances (Meyer saturation form):
     *
     *   Cgs = Cgs0·W + (2/3)·Cox·W·L
     *   Cgd = Cgd0·W
     *
     * (The Meyer triode and cutoff regions distribute Cox·W·L
     * differently between Cgs and Cgd; saturation is the most
     * common analog operating regime so Sprint 22 ships that
     * fast path. S23+ may add region-aware Meyer redistribution.)
     */
    Cgs0: z.number().nonnegative().finite().optional(),
    Cgd0: z.number().nonnegative().finite().optional(),
    Cgb0: z.number().nonnegative().finite().optional(),
    Cox: z.number().nonnegative().finite().optional(),
    /**
     * Sprint 22 D4 / D-22-1 (schema)  + Sprint 24 B2 (engine wiring) —
     * SPICE Level 2 (Grove-Frohman) parameter set. The Level 2 stamp
     * adds three effects on top of the Sprint 7 Level 1 Shichman-
     * Hodges square law:
     *
     *   Theta:  vertical-field mobility degradation     (1/V)
     *           K_eff = Kp / (1 + Theta · max(0, V_OV))
     *
     *   Eta:    static feedback / DIBL coefficient      (—)
     *           V_TH_eff = V_TH - Eta · V_DS
     *           (drain-induced barrier lowering: V_TH shrinks as
     *           V_DS grows — separate from λ which scales the I_D
     *           magnitude rather than shifting V_TH).
     *
     *   Phi:    surface inversion potential             (V)
     *           Used in the body-effect formula together with γ.
     *           V_BS = 0 (no body terminal) → body-effect term
     *           degenerates to zero, but the formula stays in the
     *           code path so a future 4-pin device just plugs in.
     *
     *   Gamma:  body-effect coefficient                 (V^0.5)
     *           V_TH_body = V_T0 + γ · (√(2·Phi - V_BS) - √(2·Phi))
     *           Sprint 24 B2 added this to the schema (was missing)
     *           so the body-effect path is type-clean. Default 0
     *           keeps Level-1 zero-diff; non-zero γ together with
     *           non-zero Phi engage the formula.
     *
     * Zero-default fast path: with all four fields unset the engine
     * stamp executes the Sprint 7 / Sprint 8a Level 1 path bit-
     * identically (verified by the Sprint 5/7 + Sprint 24 B1 frozen-
     * value regressions). Authors who want pure Shichman-Hodges
     * should leave these fields unset.
     */
    Theta: z.number().nonnegative().finite().optional(),
    Eta: z.number().nonnegative().finite().optional(),
    Phi: z.number().positive().finite().optional(),
    Gamma: z.number().nonnegative().finite().optional(),
})
    .strict();
const mosfetPins = [
    { id: "gate", side: "left", offset: 0, label: "G" },
    { id: "drain", side: "top", offset: 0, label: "D" },
    { id: "source", side: "bottom", offset: 0, label: "S" },
];
export const mosfetNmosDefinition = {
    type: "mosfet-nmos",
    category: "active",
    displayName: { tr: "MOSFET (NMOS)", en: "MOSFET (NMOS)" },
    symbol: "M",
    pins: mosfetPins,
    parameterSchema: MosfetParamsSchema,
    nonlinear: true,
};
export const mosfetPmosDefinition = {
    type: "mosfet-pmos",
    category: "active",
    displayName: { tr: "MOSFET (PMOS)", en: "MOSFET (PMOS)" },
    symbol: "M",
    pins: mosfetPins,
    parameterSchema: MosfetParamsSchema,
    nonlinear: true,
};
/**
 * Legacy alias kept for Sprint 6 fixtures. The schema migration
 * rewrites `type: "mosfet"` to `type: "mosfet-nmos"` at load time.
 * Consumers authoring new circuits should spell `mosfet-nmos` or
 * `mosfet-pmos` explicitly.
 */
export const mosfetDefinition = mosfetNmosDefinition;
//# sourceMappingURL=mosfet.js.map